import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { projectSchema, type CreateProjectRequest, type Project } from '@witty-board/shared'
import { z } from 'zod'
import { apiRequest } from './client.js'
import { tasksQueryKey, fetchAllTasks } from './tasks.js'
import { DEFAULT_DONE_WINDOW, doneSinceFor } from '../lib/done-window.js'

export const projectsQueryKey = ['projects'] as const

async function fetchProjects(): Promise<Project[]> {
  return apiRequest('/projects', z.array(projectSchema))
}

export function useProjectsQuery() {
  return useQuery({
    queryKey: projectsQueryKey,
    queryFn: fetchProjects,
    staleTime: 30_000,
  })
}

/** Precarga las tareas de un proyecto: lo que hace instantáneo pasar a su tablero (CLAUDE.md §6). */
export function prefetchProjectTasks(queryClient: QueryClient, projectId: string): void {
  // Con la ventana de Finalizada por defecto, que es la que abre el enlace de
  // la barra lateral: otra forma de filtro sería otra clave y no serviría.
  const filters = { projectId, doneSince: doneSinceFor(DEFAULT_DONE_WINDOW) }
  void queryClient.prefetchQuery({
    queryKey: tasksQueryKey(filters),
    queryFn: () => fetchAllTasks(filters),
  })
}

export function usePrefetchProjectTasks() {
  const queryClient = useQueryClient()
  return (projectId: string) => prefetchProjectTasks(queryClient, projectId)
}

export function useCreateProjectMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: CreateProjectRequest) =>
      apiRequest('/projects', projectSchema, { method: 'POST', body: request }),
    onSuccess: (created) => {
      queryClient.setQueryData<Project[]>(projectsQueryKey, (current) => [
        ...(current ?? []),
        created,
      ])
    },
  })
}

/** Papelera global (CLAUDE.md §6, fase 5): un proyecto no puede estar "dentro" de sí mismo, así que solo existe global. */
export const deletedProjectsQueryKey = ['projects', 'trash'] as const

export function useDeletedProjectsQuery() {
  return useQuery({
    queryKey: deletedProjectsQueryKey,
    queryFn: () => apiRequest('/projects?onlyDeleted=true', z.array(projectSchema)),
  })
}

export function useRestoreProjectMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiRequest(`/projects/${id}/restore`, projectSchema, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectsQueryKey })
      void queryClient.invalidateQueries({ queryKey: deletedProjectsQueryKey })
    },
  })
}
